import intlTelInput, { Iti, SomeOptions } from 'intl-tel-input';
import './styles.scss';

declare global {
  interface Window {
    translations: {
      [key: string]: string;
    };
  }
}

export interface TelInputElement extends HTMLInputElement {
  intlTelInputInstance?: any;
}

interface Options extends SomeOptions {
  /** Validates phone number on blur, focusout and keyup */
  validate?: boolean;
}

class IntlTelInput {
  settings: Options;
  #instance: Iti;

  constructor(element: TelInputElement, options: Options) {
    const defaultSettings = {
      validate: true,
      loadUtilsOnInit: async () => {
        try {
          return await import('intl-tel-input/build/js/utils');
        } catch (error) {
          console.error('Failed to load intl-tel-input utils:', error);
        }
      },
    };

    this.settings = Object.assign({}, defaultSettings, options);

    this.#instance = intlTelInput(element, this.settings);

    // Save intlTelInput instance to the input element, so that it can be accessed from other form components
    element.intlTelInputInstance = this.#instance;

    if(this.settings.validate) {
      element.addEventListener('blur', this.validate.bind(this));
      element.addEventListener('focusout', this.validate.bind(this));
      element.addEventListener('keyup', this.validate.bind(this));
    }

    if(element.form) {
      element.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    console.log('IntlTelInput.init()');
  }

  getInstance() {
    return this.#instance;
  }

  validate(event: Event) {
    const input = event.target as TelInputElement;
    const error = this.#instance.getValidationError();
    const inputContainer = input.closest('.form__field') as HTMLElement;
    let errorMessageElement = inputContainer?.querySelector('.form__error-message') as HTMLElement;
    const errorClass = 'stage-error';

    // IntlTelInput error codes are numbers, these are the possible values
    const errorCodes = {
      0: 'IS_POSSIBLE',
      1: 'INVALID_COUNTRY_CODE',
      2: 'TOO_SHORT',
      3: 'TOO_LONG',
      4: 'NOT_A_NUMBER',
      5: 'INVALID_LENGTH',
      '-99': 'UNSPECIFIED', // This is not documented, but it is used at least when typing letters (not numbers) in the input field.
    };

    console.log('validating..', {error});

    if(!errorMessageElement) {
      errorMessageElement = document.createElement('div');
      errorMessageElement.classList.add('form__error-message', 'hidden');
      errorMessageElement.id = `${input.id}-error`;
      inputContainer.append(errorMessageElement);
    }

    if(error === 0 || input.value === '') {
      input.classList.remove(errorClass);
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-errormessage');
      errorMessageElement.innerText = '';
      errorMessageElement.classList.add('hidden');
    } else if (typeof errorCodes[error] === 'string') {
      input.classList.add(errorClass);
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-errormessage', errorMessageElement.id);
      errorMessageElement.innerText = window?.translations?.[`error__phone__${errorCodes[error]}`] || errorCodes[error];
      errorMessageElement.classList.remove('hidden');
    }
  }

  onFormSubmit(event: Event) {
    const form = event.target as HTMLFormElement;

    // One form can have multiple tel inputs
    const formTelInputs = Array.from(form.elements).filter(field => !!(field as TelInputElement).intlTelInputInstance);

    const validForm = formTelInputs.every(input => !input.classList.contains('stage-error'));

    if(!validForm) {
      return false;
    }

    (formTelInputs as TelInputElement[]).forEach(input => {
      input.value = input.intlTelInputInstance.getNumber();
    });

    return true;
  }
}

export function init(input: HTMLInputElement) {
  const telInput = input as TelInputElement;
  if(telInput) {

    let country = 'us';
    let preferredCountries: string[] = [];

    const langCode = document.documentElement.lang;

    if(langCode) {
      let countryCode = langCode.split('_').pop()?.toLowerCase();

      if(countryCode === 'sv' ) {
        countryCode = 'se';
      } else if(countryCode === 'en') {
        countryCode = 'gb';
      }

      if(countryCode) {
        country = countryCode;
      }
    }

    if(telInput.dataset.language) {
      country = telInput.dataset.language?.split('_').pop()?.toLowerCase() || country;
    }

    if(telInput.dataset.preferred) {
      preferredCountries = telInput.dataset.preferred.split(',');
    }

    new IntlTelInput(telInput, {
      initialCountry: country,
      countryOrder: preferredCountries,
    });
  }
}

export default IntlTelInput;
